import { PAGINATION_SIZE } from "../../constants";
import { McpSettings, PaginationData } from "../../interfaces";
import type { SubscriptionPaymentRequiredReason } from "../../modals/subscription/subscription.interface";
import type { ProfileFormSaveError } from "../../utils/profile-form/profile-form";
import {
  CustomerMetafieldDefinitionDto,
  ApiKey,
  CampaignDto,
  CustomerData,
  CustomerDto,
  CustomerEventDto,
  Device,
  EntityTranslation,
  Feature,
  Integration,
  Link,
  Location,
  Program,
  ProgramAnalytics,
  ProgramAnalyticsAggregate,
  ProgramIntegration,
  ProgramLanguagesResponseDto,
  TemplateMetadata,
  SegmentDto,
  SubscriptionInfo,
  MinimalCoupon,
  CouponCounts,
  SectionDto,
  RewardWithCouponCodeCountsDto,
  RewardDto,
  PublicRewardDto,
  AllowedDomain,
  EngagementFlowWithProgramEngagementFlowDto,
  CouponInfoDto,
  Tier,
  UpsertTranslationDto,
  Boost,
} from "../../services/api";

export const PROGRAM_FEATURE_KEY = "program";

export type CustomersSortField = 'balance' | 'createdAt' | 'name' | 'points' | 'surname' | 'tier';

export type Coupons = Record<string, CouponInfoDto>;

export type CouponStats = Record<string, CouponCounts>;

export type Customers = Record<string, CustomerDto>;

export type CustomersData = Record<string, CustomerData>;

export type ProgramCampaigns = Record<string, CampaignDto>;

export type ProgramBoosts = Record<string, Boost>;

// Why an editor opened by URL could not load its entity: gone (404) or the request failed
export type EntityLoadError = "notFound" | "failed";


export type ProgramLocations = Record<string, Location>;

export type ProgramDevices = Record<string, Device>;

export type CampaignLinks = Record<string, Link>;

export type ApiKeys = Record<string, ApiKey>;

export type ProgramAnalyticsAggregates = Record<string, ProgramAnalyticsAggregate>;

export type ProgramFeatures = Record<string, Feature>;

export type ProgramIntegrations = Record<string, ProgramIntegration>;

export type CustomerEvents = Record<string, CustomerEventDto[]>;

export type RewardsList = Record<string, RewardWithCouponCodeCountsDto>;

export type Rewards = Record<string, RewardDto>;

// Redemption settings (type, cost, conversion) of each listed reward: the list endpoint
// only returns code counts, so they come from the public rewards endpoint.
export type RewardsDetails = Record<string, PublicRewardDto>;

/** The rewards list's view: every reward, or those with one status. */
export type RewardsView = "all" | CouponInfoDto.StatusEnum;

export type RewardListRow = RewardWithCouponCodeCountsDto & { details: PublicRewardDto | null };

// A coupon campaign with the coupon its notification link hands out, once loaded.
export type CouponCampaignRow = CampaignDto & { coupon: CouponInfoDto | null };

export type ProgramSections = Record<string, SectionDto[]>;

export type AllowedDomains = Record<string, AllowedDomain>;

export type ProgramEngagements = Record<string, EngagementFlowWithProgramEngagementFlowDto>;

export type OnboardingStatus = "idle" | "creating-program" | "program-created" | "configuring" | "done" | "error";

export interface ProgramState {
  loading: boolean;
  loadingCampaigns: boolean;
  loadingTiers: boolean;
  loadingFullScreen: boolean;
  onboardingStatus: OnboardingStatus;
  loadingCreateSubscription: boolean;
  loadingCancelSubscription: boolean;
  loadingKlaviyoAuth: boolean;
  loadingAnalytics: boolean;
  autoTranslating: boolean;
  program: Program;
  integrations: Integration[];
  integrationsSearchTerm: string;
  programIntegrations: ProgramIntegrations;
  /** The Claude (MCP) page's data, null until fetched. Not persisted: it lists staff emails. */
  mcpSettings: McpSettings | null;
  analytics: ProgramAnalytics;
  analyticsAggregate: ProgramAnalyticsAggregates;
  // Lists below are null until fetched and {} when the API returns nothing: list pages tell loading from empty with it
  customers: Customers | null;
  customersData: CustomersData;
  customersSearchTerm: string;
  customersSort: CustomersSortField;
  customersSortDirection: 'ASC' | 'DESC';
  customersPaginationData: PaginationData;
  customersEvents: CustomerEvents;
  customersEventsPaginationData: PaginationData;
  campaigns: ProgramCampaigns | null;
  campaignSlug: string;
  boosts: ProgramBoosts | null;
  boostsLoading: boolean;
  boostsLoadFailed: boolean;
  campaignLoadErrors: Record<string, EntityLoadError>;
  boostOverlapConflictId: string | null;
  locations: ProgramLocations | null;
  devices: ProgramDevices;
  programLinks: Link[];
  programSections: ProgramSections;
  campaignLinks: CampaignLinks;
  apiKeys: ApiKeys;
  features: ProgramFeatures;
  segments: SegmentDto[];
  subscriptionInfo: SubscriptionInfo;
  subscriptionInfoLoaded: boolean;
  subscriptionInfoError: boolean;
  /** Set while the API answers 402 (no active plan): every page leads back to the plan picker (`PaymentRequiredGuard`). */
  paymentRequiredReason: SubscriptionPaymentRequiredReason | null;
  formError: string;
  coupons: Coupons;
  couponsSearchTerm: string;
  couponsPaginationData: PaginationData;
  couponsFilters: CouponInfoDto.StatusEnum[];
  rewardsView: RewardsView;
  couponsSearchResults: MinimalCoupon[];
  couponStats: CouponStats;
  rewardsList: RewardsList | null;
  rewardsDetails: RewardsDetails;
  rewards: Rewards;
  tierNames: string[];
  tiers: Tier[];
  allowedDomains: AllowedDomains;
  availableDomain: boolean | null;
  engagements: ProgramEngagements | null;
  programLanguages: ProgramLanguagesResponseDto | null;
  sectionTranslations: (EntityTranslation | UpsertTranslationDto)[];
  // Variables, pipes and front-slot defaults for the template fields (design editor)
  templateMetadata: TemplateMetadata | null;
  // Its last request failed: the dialogs that need it reload it instead of waiting
  templateMetadataFailed: boolean;
  earningFlowTranslations: (EntityTranslation | UpsertTranslationDto)[];
  rewardTranslations: (EntityTranslation | UpsertTranslationDto)[];
  tierTranslations: (EntityTranslation | UpsertTranslationDto)[];
  couponTranslations: (EntityTranslation | UpsertTranslationDto)[];
  campaignTranslations: (EntityTranslation | UpsertTranslationDto)[];
  programTranslations: (EntityTranslation | UpsertTranslationDto)[];
  loadingTranslations: boolean;
  // Custom profile form (COMPLETE_USER_PROFILE): its texts in other languages, the store's metafields it can write to
  profileFormTranslations: (EntityTranslation | UpsertTranslationDto)[];
  profileFormSaveError: ProfileFormSaveError | null;
  customerMetafieldDefinitions: CustomerMetafieldDefinitionDto[] | null;
  // The program they belong to: another program's list is never shown
  customerMetafieldDefinitionsProgramId: string | null;
  loadingCustomerMetafieldDefinitions: boolean;
  customerMetafieldDefinitionsError: string | null;
  creatingCustomerMetafieldDefinition: boolean;
  createCustomerMetafieldDefinitionError: string | null;
}

export const initialState: ProgramState = {
  loading: false,
  loadingCampaigns: false,
  loadingTiers: false,
  loadingFullScreen: false,
  onboardingStatus: "idle",
  loadingCreateSubscription: false,
  loadingCancelSubscription: false,
  loadingKlaviyoAuth: false,
  loadingAnalytics: false,
  autoTranslating: false,
  program: null,
  integrations: [],
  integrationsSearchTerm: "",
  programIntegrations: {},
  mcpSettings: null,
  analytics: {},
  analyticsAggregate: {},
  customers: null,
  customersData: {},
  customersSearchTerm: "",
  customersSort: "createdAt",
  customersSortDirection: "DESC",
  customersPaginationData: {
    limit: PAGINATION_SIZE,
    page: 1,
    total: 0,
  },
  customersEvents: {},
  customersEventsPaginationData: {
    limit: 5,
    page: 1,
    total: 0,
  },
  campaigns: null,
  campaignSlug: null,
  boosts: null,
  boostsLoading: false,
  boostsLoadFailed: false,
  campaignLoadErrors: {},
  boostOverlapConflictId: null,
  locations: null,
  devices: {},
  programLinks: [],
  programSections: {
    default: [],
  },
  campaignLinks: {},
  apiKeys: {},
  features: {},
  segments: [],
  subscriptionInfo: null,
  subscriptionInfoLoaded: false,
  subscriptionInfoError: false,
  paymentRequiredReason: null,
  formError: "",
  coupons: {},
  couponsSearchTerm: "",
  couponsPaginationData: {
    limit: PAGINATION_SIZE,
    page: 1,
    total: 0,
  },
  couponsFilters: [CouponInfoDto.StatusEnum.Available, CouponInfoDto.StatusEnum.NotAvailable],
  rewardsView: "all",
  couponsSearchResults: [],
  couponStats: {},
  rewardsList: null,
  rewardsDetails: {},
  rewards: {},
  tierNames: [],
  tiers: [],
  allowedDomains: {},
  availableDomain: true,
  engagements: null,
  programLanguages: null,
  sectionTranslations: [],
  templateMetadata: null,
  templateMetadataFailed: false,
  earningFlowTranslations: [],
  rewardTranslations: [],
  tierTranslations: [],
  couponTranslations: [],
  campaignTranslations: [],
  programTranslations: [],
  loadingTranslations: false,
  profileFormTranslations: [],
  profileFormSaveError: null,
  customerMetafieldDefinitions: null,
  customerMetafieldDefinitionsProgramId: null,
  loadingCustomerMetafieldDefinitions: false,
  customerMetafieldDefinitionsError: null,
  creatingCustomerMetafieldDefinition: false,
  createCustomerMetafieldDefinitionError: null,
};

// Not campaigns: every type shares one map, so a rehydrated list of another type would read as an empty list
export const persistedKeys: (keyof ProgramState)[] = [
  "program",
  "integrations",
  "programIntegrations",
  "customers",
  "customersData",
  "locations",
  "programLinks",
  "campaignLinks",
  "apiKeys",
  "segments",
  "subscriptionInfo",
  "features",
  "coupons",
];
